import React from "react";

const DEFAULT_COLORS = {
  vestibular: "#ffffff",
  mesial: "#ffffff",
  distal: "#ffffff",
  lingual: "#ffffff",
  oclusal: "#ffffff",
};

export function toothKind(numero) {
  const digits = String(numero).replace(/\D/g, "");
  const last = Number(digits.slice(-1));
  if (last === 1 || last === 2) return "incisor";
  if (last === 3) return "canine";
  if (last === 4 || last === 5) return "premolar";
  return "molar";
}

const SHAPES = {
  incisor: {
    crown: "M25 15 Q50 7 75 15 L77 50 Q74 66 50 70 Q26 66 23 50 Z",
    roots: ["M26 50 Q50 62 74 50 C71 67 68 83 63 99 C59 111 54 121 50 126 C46 121 41 111 37 99 C32 83 29 67 26 50 Z"],
    label: "incisivo",
  },
  canine: {
    crown: "M50 7 L74 18 Q76 37 68 54 Q62 69 50 75 Q38 69 32 54 Q24 37 26 18 Z",
    roots: ["M34 58 Q50 68 66 58 C64 75 63 90 60 103 C58 112 55 118 52 123 Q50 126 48 123 C45 118 42 112 40 103 C37 90 36 75 34 58 Z"],
    label: "canino",
  },
  premolar: {
    crown: "M22 22 Q28 8 41 15 Q50 21 59 15 Q72 8 78 22 L75 54 Q69 70 50 74 Q31 70 25 54 Z",
    roots: [
      "M24 54 Q37 66 49 63 C46 78 42 93 37 108 C34 117 31 124 29 127 C26 122 25 114 26 104 C27 88 26 70 24 54 Z",
      "M51 63 Q63 66 76 54 C74 70 73 88 74 104 C75 114 74 122 71 127 C69 124 66 117 63 108 C58 93 54 78 51 63 Z",
    ],
    label: "premolar",
  },
  molar: {
    crown: "M18 23 Q20 8 36 13 Q42 5 50 12 Q58 5 64 13 Q80 8 82 23 L79 55 Q72 74 50 78 Q28 74 21 55 Z",
    roots: [
      "M20 55 Q34 69 47 66 C43 80 38 95 32 109 C28 118 25 125 22 128 C20 122 21 112 23 101 C25 84 23 67 20 55 Z",
      "M47 66 Q50 72 53 66 C55 82 54 98 51 112 C50 119 49 125 47 129 C45 125 44 119 45 112 C46 98 45 82 47 66 Z",
      "M53 66 Q66 69 80 55 C77 67 75 84 77 101 C79 112 80 122 78 128 C75 125 72 118 68 109 C62 95 57 80 53 66 Z",
    ],
    label: "molar",
  },
};

function rootShapesFor(numero, kind) {
  if (kind === "incisor" && String(numero).endsWith("1")) {
    return ["M30 53 Q50 63 70 53 C68 68 67 82 64 96 C62 106 58 115 53 121 Q50 125 47 121 C42 115 38 106 36 96 C33 82 32 68 30 53 Z"];
  }

  if (kind === "incisor" && String(numero).endsWith("2")) {
    return ["M32 54 Q50 63 68 54 C66 70 65 84 62 98 C60 108 56 116 52 121 Q49 125 46 121 C42 116 39 108 37 98 C34 84 34 70 32 54 Z"];
  }

  return SHAPES[kind].roots;
}

function SurfacePath({ face, superior, rightSide, kind }) {
  const facePaths = {
    vestibular: superior
      ? "M31 48 L50 53 L62 73 Q73 75 79 57 L69 43 Z"
      : "M19 20 Q28 9 38 14 L50 38 L31 43 Z",
    lingual: superior
      ? "M19 20 Q28 9 38 14 L50 38 L31 43 Z"
      : "M31 48 L50 53 L62 73 Q73 75 79 57 L69 43 Z",
    mesial: rightSide
      ? "M19 20 Q15 30 21 57 L31 68 L31 43 Z"
      : "M81 20 Q85 30 79 57 L69 68 L69 43 Z",
    distal: rightSide
      ? "M81 20 Q85 30 79 57 L69 68 L69 43 Z"
      : "M19 20 Q15 30 21 57 L31 68 L31 43 Z",
  };

  if (face === "oclusal") {
    const radius = kind === "molar" ? 15 : kind === "premolar" ? 12 : 9;
    return (
      <ellipse
        cx="50"
        cy="49"
        rx={radius + 8}
        ry={radius}
        fill="currentColor"
        opacity=".94"
      />
    );
  }

  return <path d={facePaths[face]} fill="currentColor" opacity=".9" />;
}

function CrownDetails({ kind, outline }) {
  if (kind === "incisor") {
    return <path d="M27 22 Q50 27 73 22 M30 48 Q50 53 70 48" fill="none" stroke={outline} strokeWidth="1.2" opacity=".72" />;
  }

  if (kind === "canine") {
    return <path d="M50 11 L50 57 M34 31 Q50 39 66 31" fill="none" stroke={outline} strokeWidth="1.2" opacity=".72" />;
  }

  if (kind === "premolar") {
    return (
      <g fill="none" stroke={outline} strokeWidth="1.2" opacity=".72">
        <path d="M30 25 Q40 38 50 29 Q60 38 70 25" />
        <path d="M34 59 Q43 47 50 57 Q57 47 66 59" />
      </g>
    );
  }

  return (
    <g fill="none" stroke={outline} strokeWidth="1.2" opacity=".72">
      <path d="M28 25 Q39 39 50 27 Q61 39 72 25" />
      <path d="M29 59 Q40 47 50 59 Q60 47 71 59" />
      <path d="M50 20 L50 66" />
    </g>
  );
}

function RootDetails({ kind, outline }) {
  if (kind === "incisor" || kind === "canine") {
    return (
      <path
        d="M50 68 C49 86 48 105 50 122"
        fill="none"
        stroke={outline}
        strokeWidth="1.05"
        strokeLinecap="round"
        opacity=".55"
      />
    );
  }

  if (kind === "premolar") {
    return (
      <g fill="none" stroke={outline} strokeWidth="1.05" opacity=".5">
        <path d="M38 66 Q38 86 30 120" />
        <path d="M62 66 Q62 86 70 120" />
      </g>
    );
  }

  return (
    <g fill="none" stroke={outline} strokeWidth="1.05" opacity=".5">
      <path d="M35 68 Q33 89 25 121" />
      <path d="M50 72 Q49 95 47 124" />
      <path d="M65 68 Q67 89 75 121" />
    </g>
  );
}

export default function AnatomicalTooth({
  numero,
  colors = DEFAULT_COLORS,
  selected = false,
  absent = false,
  compact = false,
  showNumber = true,
}) {
  const kind = toothKind(numero);
  const shape = SHAPES[kind];
  const rootPaths = rootShapesFor(numero, kind);
  const digits = String(numero).replace(/\D/g, "");
  const quadrant = Number(digits.slice(0, 1));
  const superior = [1, 2, 5, 6].includes(quadrant) || digits.length === 1;
  const rightSide = [1, 4, 5, 8].includes(quadrant);
  const id = `tooth-${String(numero).replace(/\W/g, "")}-${kind}`;
  const palette = { ...DEFAULT_COLORS, ...colors };
  const hasFinding = Object.values(palette).some((color) => color && color !== "#ffffff");
  const outline = selected ? "#0ea5e9" : absent ? "#94a3b8" : hasFinding ? "#64748b" : "#9fb1c7";

  return (
    <svg
      viewBox="0 0 100 144"
      className="vd-anatomical-tooth"
      aria-hidden="true"
      style={{ width: compact ? 54 : 66, height: compact ? 74 : 90 }}
    >
      <defs>
        <clipPath id={id}>
          <path d={shape.crown} />
        </clipPath>
      </defs>

      <g transform={superior ? "rotate(180 50 66)" : undefined}>
        <g fill="#edf4fa" stroke={outline} strokeWidth={selected ? 3.4 : 1.7} strokeLinejoin="round">
          {rootPaths.map((root) => <path key={root} d={root} />)}
        </g>

        <g fill="#f8fbff" stroke={outline} strokeWidth={selected ? 3.4 : 1.7} strokeLinejoin="round">
          <path d={shape.crown} />
        </g>

        <g clipPath={`url(#${id})`} stroke={outline} strokeWidth=".8">
          {Object.entries(palette).map(([face, color]) => (
            <g key={face} style={{ color }}>
              <SurfacePath face={face} superior={superior} rightSide={rightSide} kind={kind} />
            </g>
          ))}
        </g>

        <path d={shape.crown} fill="none" stroke={outline} strokeWidth={selected ? 3.4 : 1.7} strokeLinejoin="round" />
        <CrownDetails kind={kind} outline={outline} />
        <RootDetails kind={kind} outline={outline} />

        {absent && (
          <path d="M22 20 L78 112 M78 20 L22 112" stroke="#64748b" strokeWidth="4" strokeLinecap="round" />
        )}
      </g>

      {showNumber && (
        <text x="50" y="141" textAnchor="middle" fontSize={compact ? 11 : 13} fontWeight="900" letterSpacing=".2" fill="#1e3a5f">
          {numero}
        </text>
      )}
    </svg>
  );
}
